/**
 * <diameter-range-slider data-skip-subtree-update>
 *
 * Dual-handle range slider that sits on top of the existing discrete diameter
 * facet checkboxes (custom.filter_diameter). Shopify does NOT support native
 * range (.gte/.lte) filtering on metafields, so this is a UI layer: dragging the
 * handles checks every diameter value within [min, max] and unchecks the rest,
 * then triggers the theme's own facet-inputs-component.updateFilters() so the
 * normal Section Rendering AJAX flow runs (same path as the checkboxes).
 *
 * IMPORTANT: the element MUST carry data-skip-subtree-update. The theme re-renders
 * collection sections through morph (assets/morph.js) on lazy hydration and on
 * every filter change. Without that attribute, morph reconciles this element
 * against the empty server markup and wipes the JS-built UI (empty panel bug).
 *
 * Robustness:
 * - Never caches checkbox nodes; morph may replace them, so we re-query the
 *   sibling checkboxes from the facet-inputs-component each time we read/write.
 * - Listens to the theme 'filter:update' event to re-sync the handles after any
 *   filter change it didn't cause (clear-all, another facet, back/forward).
 * - Self-initializing: connectedCallback reads the server-checked checkboxes, so a
 *   full re-init also lands in the correct state.
 */
class DiameterRangeSlider extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;

    // The slider is rendered as a sibling of facet-inputs-component (inside the
    // panel) so its range-input change events do NOT hit the theme's capture-phase
    // on:change delegation. Find the facet-inputs-component within the same panel.
    const panel = this.closest('.facets__inputs--diameter-slider');
    this.source = panel ? panel.querySelector('facet-inputs-component') : null;
    if (!this.source) return;

    this.unit = this.dataset.unit || '';
    this.vals = this.#values();
    if (this.vals.length === 0) return;

    this.#build();
    this._ready = true;

    // Re-sync the handles whenever morph rewrites the checkbox `checked` attributes
    // (clear-all, removing another facet, browser back/forward). morph sets the
    // attribute itself (assets/morph.js: updateInput -> updateAttribute), so this
    // fires AFTER the section re-render with the authoritative state. Our own #apply
    // only sets the `.checked` property (not the attribute), so it never triggers
    // this observer -> no feedback loop.
    this._observer = new MutationObserver(() => this.#resync());
    this._observer.observe(this.source, { subtree: true, attributes: true, attributeFilter: ['checked'] });
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }

  /** Live, fresh list of the facet's checkboxes (morph may replace these nodes). */
  #boxes() {
    return this.source ? Array.from(this.source.querySelectorAll('input[type="checkbox"]')) : [];
  }

  /** Sorted unique numeric diameter values read from the checkboxes. */
  #values() {
    const seen = new Set();
    for (const cb of this.#boxes()) {
      const v = parseFloat(cb.value);
      if (!Number.isNaN(v)) seen.add(v);
    }
    return Array.from(seen).sort((a, b) => a - b);
  }

  /** [min, max] of currently-checked values, or null if none are checked. */
  #checkedRange() {
    const checked = this.#boxes()
      .filter((cb) => cb.checked)
      .map((cb) => parseFloat(cb.value))
      .filter((v) => !Number.isNaN(v))
      .sort((a, b) => a - b);
    return checked.length ? [checked[0], checked[checked.length - 1]] : null;
  }

  /** Format a value: integers stay integer ("40"), decimals stay ("45.5"). */
  #fmt(v) {
    return String(v);
  }

  #build() {
    const last = this.vals.length - 1;
    const range = this.#checkedRange();
    this.minIdx = range ? Math.max(0, this.vals.indexOf(range[0])) : 0;
    this.maxIdx = range ? this.vals.indexOf(range[1]) : last;
    if (this.maxIdx < 0) this.maxIdx = last;

    this.innerHTML = `
      <div class="diameter-slider__values">
        <span class="diameter-slider__value diameter-slider__value--min"></span>
        <span class="diameter-slider__sep">–</span>
        <span class="diameter-slider__value diameter-slider__value--max"></span>
      </div>
      <div class="diameter-slider__track">
        <div class="diameter-slider__rail"></div>
        <div class="diameter-slider__fill"></div>
        <input class="diameter-slider__range diameter-slider__range--min" type="range"
               min="0" max="${last}" step="1" value="${this.minIdx}" aria-label="Minimale diameter (${this.unit})">
        <input class="diameter-slider__range diameter-slider__range--max" type="range"
               min="0" max="${last}" step="1" value="${this.maxIdx}" aria-label="Maximale diameter (${this.unit})">
      </div>`;

    this.minInput = this.querySelector('.diameter-slider__range--min');
    this.maxInput = this.querySelector('.diameter-slider__range--max');
    this.fill = this.querySelector('.diameter-slider__fill');
    this.minLabel = this.querySelector('.diameter-slider__value--min');
    this.maxLabel = this.querySelector('.diameter-slider__value--max');

    if (this.vals.length === 1) {
      this.minInput.disabled = true;
      this.maxInput.disabled = true;
    }

    this.minInput.addEventListener('input', this.#onInput);
    this.maxInput.addEventListener('input', this.#onInput);
    // 'change' fires on pointer release / keyboard commit -> apply + run the filter.
    this.minInput.addEventListener('change', this.#onChange);
    this.maxInput.addEventListener('change', this.#onChange);

    this.#render();
  }

  /** Live update while dragging: clamp crossover, keep the active handle on top, redraw. */
  #onInput = (event) => {
    let lo = parseInt(this.minInput.value, 10);
    let hi = parseInt(this.maxInput.value, 10);

    if (lo > hi) {
      if (event.target === this.minInput) {
        lo = hi;
        this.minInput.value = String(lo);
      } else {
        hi = lo;
        this.maxInput.value = String(hi);
      }
    }

    this.minIdx = lo;
    this.maxIdx = hi;

    this.minInput.style.zIndex = event.target === this.minInput ? '5' : '4';
    this.maxInput.style.zIndex = event.target === this.maxInput ? '5' : '4';

    this.#render();
  };

  #onChange = () => this.#apply();

  #render() {
    const last = this.vals.length - 1 || 1;
    const minPct = (this.minIdx / last) * 100;
    const maxPct = (this.maxIdx / last) * 100;

    this.fill.style.insetInlineStart = `${minPct}%`;
    this.fill.style.insetInlineEnd = `${100 - maxPct}%`;

    this.minLabel.textContent = `${this.#fmt(this.vals[this.minIdx])} ${this.unit}`.trim();
    this.maxLabel.textContent = `${this.#fmt(this.vals[this.maxIdx])} ${this.unit}`.trim();
  }

  /** Apply the selected range to the (freshly queried) checkboxes and run the filter. */
  #apply() {
    const last = this.vals.length - 1;
    const lo = this.vals[this.minIdx];
    const hi = this.vals[this.maxIdx];
    const isFullRange = this.minIdx === 0 && this.maxIdx === last;

    for (const cb of this.#boxes()) {
      const v = parseFloat(cb.value);
      if (Number.isNaN(v)) continue;
      cb.checked = isFullRange ? false : v >= lo && v <= hi;
    }

    if (this.source && typeof this.source.updateFilters === 'function') {
      this.source.updateFilters();
    }

    // Set after updateFilters(): the theme's updateSelectedFacetSummary() runs
    // inside it and would otherwise leave the count showing instead of the range.
    this.#updateSummary(isFullRange, lo, hi);
  }

  /** Re-sync handles + summary from the current checkbox state (clear-all, other facets, back nav). */
  #resync() {
    if (!this.minInput) return;

    const last = this.vals.length - 1;
    const range = this.#checkedRange();

    if (!range) {
      this.minIdx = 0;
      this.maxIdx = last;
    } else {
      this.minIdx = Math.max(0, this.vals.indexOf(range[0]));
      this.maxIdx = this.vals.indexOf(range[1]);
      if (this.maxIdx < 0) this.maxIdx = last;
    }

    this.minInput.value = String(this.minIdx);
    this.maxInput.value = String(this.maxIdx);
    this.#render();
    this.#updateSummary(this.minIdx === 0 && this.maxIdx === last, this.vals[this.minIdx], this.vals[this.maxIdx]);
  }

  /** Show the range in the facet summary instead of the count of checked values. */
  #updateSummary(isFullRange, lo, hi) {
    const details = this.closest('details');
    const status = details && details.querySelector('facet-status-component [ref="facetStatus"]');
    if (!status) return;

    status.classList.remove('bubble', 'facets__bubble');
    status.textContent = isFullRange ? '' : `${this.#fmt(lo)} – ${this.#fmt(hi)} ${this.unit}`.trim();
  }
}

if (!customElements.get('diameter-range-slider')) {
  customElements.define('diameter-range-slider', DiameterRangeSlider);
}
